/**
 * 移入批次 / 移出批次抽屉共用的小组件：号码输入区、「已在批次」标注、号码复制。
 */
import { Button, Input, message } from 'antd';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { copyToClipboard } from '../utils/numberInputUtils';

/** 抽屉宽度：原型为 min(780px, 92%)，移入表格列标题会折行，放宽到 1000px 让列标题一行展示 */
export const MOVE_DRAWER_WIDTH = 'min(1000px, 92vw)';

interface MoveNumberInputProps {
    /** 文案命名空间 */
    ns: 'moveIn' | 'moveOut';
    value: string;
    /** 解析摘要，未解析时为空 */
    summary?: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    onSubmit: () => void;
    /** 只接受包牌号（标签与占位文案改为包牌号） */
    bagOnly?: boolean;
}

/** 号码输入区：多行文本框 + 提交解析 + 解析摘要 */
export const MoveNumberInput: React.FC<MoveNumberInputProps> = ({ ns, value, summary, disabled, onChange, onSubmit, bagOnly }) => {
    const { t } = useTranslation();
    const prefix = `wms.sortingBatch.${ns}.${bagOnly ? 'bagNumbers' : 'numbers'}`;
    return (
        <div className="flex items-start gap-3">
            <span className="w-24 flex-none pt-1.5 text-[13px] text-[#4e5969]">{t(`${prefix}Label`)}</span>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Input.TextArea
                    className="font-mono text-[12.5px] leading-[1.8]"
                    style={{ height: 150, resize: 'none' }}
                    value={value}
                    disabled={disabled}
                    placeholder={t(`${prefix}Placeholder`) ?? undefined}
                    onChange={(e) => onChange(e.target.value)}
                />
                <div className="flex items-center gap-2.5">
                    <Button type="primary" disabled={disabled} onClick={onSubmit}>
                        {t(`wms.sortingBatch.${ns}.submitParse`)}
                    </Button>
                    {summary ? <span className="text-[12.5px] text-[#86909c]">{summary}</span> : null}
                </div>
            </div>
        </div>
    );
};

/** 「已在批次 X」标注 */
export const AlreadyInBatch: React.FC<{ batchNumber: string }> = ({ batchNumber }) => {
    const { t } = useTranslation();
    return (
        <span className="inline-flex flex-none items-center gap-1 whitespace-nowrap text-[11.5px] font-medium text-[#c26b00]">
            {t('wms.sortingBatch.common.alreadyInBatch')}
            <span className="font-mono font-semibold">{batchNumber}</span>
        </span>
    );
};

/** 复制号码到剪贴板（换行拼接）：成功提示复制条数，失败提示复制失败 */
export const useCopyNumbers = (): ((numbers: string[]) => void) => {
    const { t } = useTranslation();
    return useCallback((numbers: string[]) => {
        if (!numbers.length) return;
        copyToClipboard(numbers.join('\n')).then(
            () => { message.success(t('wms.sortingBatch.common.copied', { count: numbers.length })); },
            () => { message.error(t('wms.sortingBatch.common.copyFailed')); },
        );
    }, [t]);
};
